import type {
  AuthIdentity,
  AuthResponse,
  AuthUser,
  LoginInput,
  RegisterInput,
  RegisterPendingResponse,
  RegisterResponse,
  TelegramWidgetAuthInput,
} from "../WarehouseApi";
import type { WarehouseApiModuleContext } from "./WarehouseApiModuleContext";

type ExtractApiError = (payload: unknown) => string | null;
type NormalizeAuthResponse = (payload: unknown) => AuthResponse | null;
type NormalizeAuthUser = (payload: unknown) => AuthUser;
type NormalizeAuthIdentity = (payload: unknown) => AuthIdentity;
type PersistSessionFromAuth = (response: AuthResponse) => void;
type ClearStoredSession = () => void;
type GetStoredRefreshToken = () => string | null;

interface AuthApiModuleDependencies {
  extractApiError: ExtractApiError;
  normalizeAuthResponse: NormalizeAuthResponse;
  normalizeAuthUser: NormalizeAuthUser;
  normalizeAuthIdentity: NormalizeAuthIdentity;
  persistSessionFromAuth: PersistSessionFromAuth;
  clearStoredSession: ClearStoredSession;
  getStoredRefreshToken: GetStoredRefreshToken;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export class AuthApiModule {
  constructor(
    private readonly context: Pick<WarehouseApiModuleContext, "sendJson">,
    private readonly deps: AuthApiModuleDependencies,
  ) {}

  async login(input: LoginInput): Promise<AuthResponse> {
    const payload = await this.context.sendJson<unknown>(
      "/api/auth/login",
      "POST",
      {
        username: input.username.trim(),
        password: input.password,
      },
    );

    const normalized = this.deps.normalizeAuthResponse(payload);
    if (!normalized) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(apiError || "Некорректный ответ сервера при входе");
    }

    this.deps.persistSessionFromAuth(normalized);
    return normalized;
  }

  async loginWithTelegram(
    initData: string,
  ): Promise<AuthResponse | RegisterPendingResponse> {
    const payload = await this.context.sendJson<unknown>(
      "/api/auth/telegram",
      "POST",
      {
        initData: initData.trim(),
      },
    );

    const pendingResponse = this.normalizePendingResponse(payload);
    if (pendingResponse) {
      return pendingResponse;
    }

    const normalized = this.deps.normalizeAuthResponse(payload);
    if (!normalized) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при Telegram-входе",
      );
    }

    this.deps.persistSessionFromAuth(normalized);
    return normalized;
  }

  async loginWithTelegramWidget(
    payload: TelegramWidgetAuthInput,
  ): Promise<AuthResponse | RegisterPendingResponse> {
    const responsePayload = await this.context.sendJson<unknown>(
      "/api/auth/telegram/widget",
      "POST",
      {
        widgetUser: payload,
      },
    );

    const pendingResponse = this.normalizePendingResponse(responsePayload);
    if (pendingResponse) {
      return pendingResponse;
    }

    const normalized = this.deps.normalizeAuthResponse(responsePayload);
    if (!normalized) {
      const apiError = this.deps.extractApiError(responsePayload);
      throw new Error(
        apiError || "Некорректный ответ сервера при Telegram-входе (widget)",
      );
    }

    this.deps.persistSessionFromAuth(normalized);
    return normalized;
  }

  async loginWithGoogleIdToken(
    idToken: string,
  ): Promise<AuthResponse | RegisterPendingResponse> {
    const payload = await this.context.sendJson<unknown>(
      "/api/auth/google",
      "POST",
      {
        idToken: idToken.trim(),
      },
    );

    const pendingResponse = this.normalizePendingResponse(payload);
    if (pendingResponse) {
      return pendingResponse;
    }

    const normalized = this.deps.normalizeAuthResponse(payload);
    if (!normalized) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при входе через Google",
      );
    }

    this.deps.persistSessionFromAuth(normalized);
    return normalized;
  }

  async loginWithAppleIdToken(
    idToken: string,
  ): Promise<AuthResponse | RegisterPendingResponse> {
    const payload = await this.context.sendJson<unknown>(
      "/api/auth/apple",
      "POST",
      {
        idToken: idToken.trim(),
      },
    );

    const pendingResponse = this.normalizePendingResponse(payload);
    if (pendingResponse) {
      return pendingResponse;
    }

    const normalized = this.deps.normalizeAuthResponse(payload);
    if (!normalized) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при входе через Apple",
      );
    }

    this.deps.persistSessionFromAuth(normalized);
    return normalized;
  }

  async register(input: RegisterInput): Promise<RegisterResponse> {
    const payload = await this.context.sendJson<unknown>(
      "/api/auth/register",
      "POST",
      {
        username: input.username.trim(),
        email:
          typeof input.email === "string" && input.email.trim()
            ? input.email.trim()
            : undefined,
        password: input.password,
        photo: input.photo?.trim() || undefined,
      },
    );

    const pendingResponse = this.normalizePendingResponse(payload);
    if (pendingResponse) {
      return pendingResponse;
    }

    const normalized = this.deps.normalizeAuthResponse(payload);
    if (!normalized) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(apiError || "Некорректный ответ сервера при регистрации");
    }

    this.deps.persistSessionFromAuth(normalized);
    return normalized;
  }

  async getAuthIdentities(): Promise<AuthIdentity[]> {
    const payload = await this.context.sendJson<unknown>(
      "/api/auth/identities",
      "GET",
    );
    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !Array.isArray(payload.data)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при загрузке identities",
      );
    }

    return payload.data.map((item) => this.deps.normalizeAuthIdentity(item));
  }

  async linkEmailIdentity(email: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      "/api/auth/link/email",
      "POST",
      {
        email: email.trim(),
      },
    );
    this.ensureSuccess(payload, "Не удалось привязать email");
  }

  async linkTelegramIdentity(initData: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      "/api/auth/link/telegram",
      "POST",
      {
        initData: initData.trim(),
      },
    );
    this.ensureSuccess(payload, "Не удалось привязать Telegram");
  }

  async linkTelegramIdentityWidget(
    payload: TelegramWidgetAuthInput,
  ): Promise<void> {
    const responsePayload = await this.context.sendJson<unknown>(
      "/api/auth/link/telegram/widget",
      "POST",
      {
        widgetUser: payload,
      },
    );
    this.ensureSuccess(responsePayload, "Не удалось привязать Telegram");
  }

  async linkGoogleIdentity(idToken: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      "/api/auth/link/google",
      "POST",
      {
        idToken: idToken.trim(),
      },
    );
    this.ensureSuccess(payload, "Не удалось привязать Google");
  }

  async linkAppleIdentity(idToken: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      "/api/auth/link/apple",
      "POST",
      {
        idToken: idToken.trim(),
      },
    );
    this.ensureSuccess(payload, "Не удалось привязать Apple");
  }

  async unlinkIdentity(provider: AuthIdentity["provider"]): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      `/api/auth/link/${provider}`,
      "DELETE",
    );
    this.ensureSuccess(payload, "Не удалось отвязать identity");
  }

  async logout(): Promise<void> {
    const refreshToken = this.deps.getStoredRefreshToken();

    try {
      await this.context.sendJson<unknown>(
        "/api/auth/logout",
        "POST",
        refreshToken ? { refreshToken } : undefined,
      );
    } finally {
      this.deps.clearStoredSession();
    }
  }

  private ensureSuccess(payload: unknown, fallbackMessage: string): void {
    if (!isRecord(payload) || payload.success !== true) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(apiError || fallbackMessage);
    }
  }

  private normalizePendingResponse(
    payload: unknown,
  ): RegisterPendingResponse | null {
    if (!isRecord(payload) || payload.pending !== true) {
      return null;
    }

    const message =
      typeof payload.message === "string" && payload.message.trim()
        ? payload.message.trim()
        : "Заявка создана. Дождитесь одобрения администратора.";

    return {
      pending: true,
      message,
      user: this.deps.normalizeAuthUser(payload.user),
    };
  }
}
